var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from "moment";
import { useRef, useState } from "react";
import useSWRMutation from "swr/mutation";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import useToast from "../../hooks/useToast";
import { AUTH_SWR_KEYS } from "../Utilities/swrKeys";
import { useCachedSWR } from "../Utilities/useCachedSWR";
import { SkipDayLoadingText } from "./skipDays-types";
import { buildMutationToastStrings, groupSkipDaysByMonth, updateSkipDay } from "./skipDaysQuery-utils";
const DEBOUNCE_MS = 300;
/**
 * This hook reads skip day configurations and all available skip days for a customer from the cache.
 * Data is hydrated by AuthenticatedDataHydrator on login.
 *
 * Supports debounced batching: multiple day toggles within DEBOUNCE_MS are sent in a single request.
 */
export const useSkipDaysQuery = () => {
    var _a, _b, _c;
    const toast = useToast();
    const { datadogLog } = useLogging();
    //* ===== DEBOUNCE REFS =====
    const pendingBatchRef = useRef([]);
    const debounceTimerRef = useRef(null);
    //* ===== ANIMATION STATE =====
    const [mutatingDates, setMutatingDates] = useState(new Set());
    //* ===== SWR QUERY =====
    const { data, error, isLoading, mutate: updateCache } = useCachedSWR(AUTH_SWR_KEYS.SKIP_DAYS, {
        onSuccess: (data) => {
            if (!data.success) {
                const errorMessage = new Error("Skip Days fetch error");
                datadogLog("Skip Days fetch error", LogLevel.ERROR, undefined, errorMessage);
            }
        },
        onError: (error) => {
            datadogLog("Skip Days error", LogLevel.ERROR, undefined, error);
        },
    });
    //* ===== SWR MUTATION =====
    const { trigger: triggerUpdate } = useSWRMutation(AUTH_SWR_KEYS.SKIP_DAYS, updateSkipDay, {
        onError: (error) => {
            datadogLog("Skip Days update error", LogLevel.ERROR, undefined, error);
        },
        populateCache: (result, currentData) => {
            var _a;
            if (!(result === null || result === void 0 ? void 0 : result.skipDays))
                return currentData;
            // The server returns the complete, authoritative skip days list in every response.
            // Using it directly avoids a race condition where concurrent mutations each receive
            // a stale currentData snapshot and overwrite each other's changes on merge.
            return {
                success: result.success,
                configuration: ((_a = currentData === null || currentData === void 0 ? void 0 : currentData.configuration) !== null && _a !== void 0 ? _a : result.configuration),
                skipDays: result.skipDays,
            };
        },
        revalidate: false,
    });
    //* ===== BATCH FIRE (called after debounce) =====
    const fireBatchUpdate = () => __awaiter(void 0, void 0, void 0, function* () {
        const batch = [...pendingBatchRef.current];
        pendingBatchRef.current = [];
        debounceTimerRef.current = null;
        if (!batch.length)
            return;
        let snapshot;
        try {
            // Apply optimistic update via the bound useSWR mutate rather than useSWRMutation's
            // optimisticData. The bound mutate reads the CURRENT cache on every call, so concurrent
            // batch fires correctly stack on top of each other instead of each reverting the previous.
            // The snapshot is captured here (not from the outer closure) to avoid stale data.
            updateCache((currentData) => {
                snapshot = currentData;
                if (!(currentData === null || currentData === void 0 ? void 0 : currentData.skipDays))
                    return currentData;
                return Object.assign(Object.assign({}, currentData), { skipDays: currentData.skipDays.map((day) => {
                        const batchEntry = batch.find((b) => b.date.utc().isSame(day.customerDate, "day"));
                        return batchEntry ? Object.assign(Object.assign({}, day), { skip: batchEntry.skip }) : day;
                    }) });
            }, { revalidate: false });
            const result = yield triggerUpdate({ updates: batch }, { revalidate: false });
            batch.forEach(({ date, skip }) => {
                var _a, _b;
                const dayResult = (_a = result === null || result === void 0 ? void 0 : result.results) === null || _a === void 0 ? void 0 : _a.find((r) => moment(r.customerDate).utc().isSame(date, "day"));
                const succeeded = (_b = dayResult === null || dayResult === void 0 ? void 0 : dayResult.success) !== null && _b !== void 0 ? _b : true;
                buildMutationToastStrings(date, skip, succeeded, toast);
            });
        }
        catch (_d) {
            // Rollback the optimistic update so the calendar reflects the pre-click state.
            if (snapshot !== undefined) {
                updateCache(snapshot, { revalidate: false });
            }
            batch.forEach(({ date, skip }) => buildMutationToastStrings(date, skip, false, toast));
        }
        finally {
            setMutatingDates((prev) => {
                const next = new Set(prev);
                batch.forEach(({ date }) => next.delete(date.utc().format("YYYY-MM-DD")));
                return next;
            });
        }
    });
    //* ===== PUBLIC TRIGGER =====
    const triggerUpdateSkipDay = (date, skip) => {
        const dateMoment = moment.isMoment(date) ? date : moment(date);
        // Last click wins for the same day within the debounce window
        pendingBatchRef.current = [
            ...pendingBatchRef.current.filter((u) => !u.date.utc().isSame(dateMoment, "day")),
            { date: dateMoment, skip },
        ];
        setMutatingDates((prev) => new Set([...prev, dateMoment.utc().format("YYYY-MM-DD")]));
        if (debounceTimerRef.current)
            clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(fireBatchUpdate, DEBOUNCE_MS);
    };
    return {
        allSkipDays: data === null || data === void 0 ? void 0 : data.skipDays,
        configuration: data === null || data === void 0 ? void 0 : data.configuration,
        error: (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : "",
        hasSkipDays: !!((_b = data === null || data === void 0 ? void 0 : data.skipDays) === null || _b === void 0 ? void 0 : _b.length),
        isLoading,
        loadingText: SkipDayLoadingText.LOADING,
        mutatingDates,
        skipDaysByMonth: groupSkipDaysByMonth(data === null || data === void 0 ? void 0 : data.skipDays),
        skippedDays: (_c = data === null || data === void 0 ? void 0 : data.skipDays) === null || _c === void 0 ? void 0 : _c.filter((skipDay) => skipDay.skip === true),
        triggerUpdateSkipDay,
    };
};
